import React from 'react';
import styled from 'styled-components';
import { formatDateToDay } from 'src/utils/dateUtils';
import {
  FLIGHT_TYPES,
  FLIGHT_TYPES_NAME,
} from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import { Flight } from '@lt/components/icons-react';
import { useTranslation } from '@lt/localization';
import { Charter as FlightIconUnknownType } from '@lt/components/icons-react/checkout-flight-ticket';

import type { SkeletonFlightInfo } from '../../types';
import FlightsLuggageIconWithLabel from '../../../Luggage/FlightsLuggage/components/FlightsLuggageIconWithLabel';
import FlightDuration from '../../../FlightDuration/FlightDuration';
import LabelWithDescription from '../../../LabelsContainer/components/LabelWithDescription';

type Props = {
  item: SkeletonFlightInfo;
  isMobile: boolean;
  direction: 'to' | 'back';
};

const SkeletonDetailsItem = (props: Props) => {
  const { item, isMobile, direction } = props;

  const { title } = FLIGHT_TYPES[item.flightType];

  const { t } = useTranslation();

  const directionTitle =
    direction === 'to' ? t('checkout.flight.to') : t('checkout.flight.back');

  return (
    <Wrapper $isMobile={isMobile}>
      {!isMobile ? <Title>{directionTitle}</Title> : null}
      <Content $isMobile={isMobile} $isDirectionTo={direction === 'to'}>
        {isMobile ? <MobileTitle>{directionTitle}</MobileTitle> : null}
        <Luggage>
          <FlightsLuggageIconWithLabel type="hand" />
          <FlightsLuggageIconWithLabel type="hold" />
          <FlightsLuggageIconWithLabel type="ski_equipment" />
        </Luggage>
        <Airports>
          <Duration $isMobile={isMobile}>
            <Line $isMobile={isMobile} />
            <FlightDurationContainer>
              <FlightDuration isMobile={false} direct={false} isAdditional />
            </FlightDurationContainer>
            <Line $isMobile={isMobile} />
          </Duration>
          <DetailsContainer>
            <AirportDetails $isMobile={isMobile}>
              <div>{formatDateToDay(item.departure)}</div>
              <div>{item.departureCity}</div>
            </AirportDetails>
            <AirportDetails $isMobile={isMobile}>
              <div>{formatDateToDay(item.arrival)}</div>
              <div>{item.arrivalCity}</div>
            </AirportDetails>
          </DetailsContainer>
        </Airports>
        <LabelWithDescription
          icon={
            item.flightType === FLIGHT_TYPES_NAME.NA ? (
              <FlightIconUnknownType height={24} />
            ) : (
              <IconBackgroundFlight>
                <Flight height={18} />
              </IconBackgroundFlight>
            )
          }
          text={title}
        />
      </Content>
    </Wrapper>
  );
};

const MobileTitle = styled.div`
  color: #919499;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 11px;
`;

const IconBackgroundFlight = styled.div`
  border: 1px solid #f8e71c;
  border-radius: 20px;
  width: 24px;
  height: 24px;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  ${({ $isMobile }) =>
    !$isMobile &&
    `
    height: 165px;
    padding: 8px 0;
  `}
`;

const Content = styled.div<{ $isMobile: boolean; $isDirectionTo: boolean }>`
  ${({ $isMobile, $isDirectionTo }) =>
    !$isMobile &&
    `
    display: flex;
    flex-direction: column;
    justify-content: flex-${$isDirectionTo ? `end` : `start`}
  `}
`;

const Luggage = styled.div`
  display: flex;

  opacity: 0.5;

  &:first-child {
    margin-left: -4px;
  }

  > * + * {
    margin-left: -4px;
  }
`;

const Airports = styled.div`
  width: fit-content;
  margin-bottom: 12px;
`;

const AirportDetails = styled.div<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? '12' : '14')}px;

  color: #c2c5cc;
`;

const Line = styled.hr<{ $isMobile: boolean }>`
  border: none;
  background: #c2c5cc;
  height: 1px;

  width: ${(props) => (props.$isMobile ? '60' : '72')}px;

  margin: ${(props) => (props.$isMobile ? '13px 0px' : '17px 0px')};
`;

const Duration = styled.div<{ $isMobile: boolean }>`
  display: flex;

  > * + * {
    margin-left: ${(props) => (props.$isMobile ? '4px' : '8px')};
  }
`;

const FlightDurationContainer = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.5;
`;

const Title = styled.div`
  margin-right: 2px;

  width: 16px;
  transform: rotate(270deg) translateY(65px) translateX(-20px);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #c2c5cc;
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default SkeletonDetailsItem;
